import {
  Dashboard as DashboardIcon,
  ExpandLess,
  ExpandMore,
  Settings as SettingsIcon,
  ShoppingCart,
  Collections as CollectionsIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
  Article as ArticleIcon,
  AreaChart as AreaChartIcon,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import React, { useMemo } from "react";
import { NavLink, useNavigate } from "react-router";
import { useRouter } from "../context/RouterContext";
import { useUserContext } from "../context/UserContext";
interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  variant?: "permanent" | "persistent" | "temporary";
}

const sidebarWidth = 280;

const menuItems = [
  {
    id: "vendite",
    label: "Vendite",
    icon: <DashboardIcon />,
    page: "vendite" as const,
  },
  {
    id: "spedizioni",
    label: "Spedizioni",
    icon: <ShoppingCart />,
    page: "spedizioni" as const,
  },
  {
    id: "scheda-punto-vendita",
    label: "Scheda Punto Vendita",
    icon: <ArticleIcon />,
    page: "scheda-punto-vendita" as const,
  },
  {
    id: "immagini-punto-vendita",
    label: "Immagini Punto Vendita",
    icon: <AddPhotoAlternateIcon />,
    page: "immagini-punto-vendita" as const,
  },
  {
    id: "repository-immagini",
    label: "Repository Immagini",
    icon: <CollectionsIcon />,
    page: "repository-immagini" as const,
  },
  {
    id: "statistiche",
    label: "Statistiche",
    icon: <AreaChartIcon />,
    page: "statistiche" as const,
    children: [
      {
        id: "statistiche-cliente",
        label: "Statistiche Cliente",
        children: [
          {
            id: "ordinato",
            label: "Ordinato",
            page: "statistiche/cliente/ordinato",
          },
          {
            id: "bestseller",
            label: "Art. Bestseller",
            page: "statistiche/cliente/bestseller",
          },
        ],
      },
      {
        id: "statistiche-agente",
        label: "Statistiche Agente",
        children: [
          {
            id: "visitatori",
            label: "Visitatori / Sessioni",
            page: "statistiche/agente/visitatori",
          },
          {
            id: "acquisti",
            label: "Acquisti",
            page: "statistiche/agente/acquisti",
          },
          {
            id: "carrelli-abbandonati",
            label: "Carrelli abbandonati",
            page: "statistiche/agente/carrelli-abbandonati",
          },
        ],
      },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  variant = "persistent",
}) => {
  const { isLoading, user } = useUserContext();
  
  const filteredMenuItems = useMemo(() => {
    const onlyCustomerItems = ["spedizioni"];
    // Items that should be hidden from Customer (Agent only)
    const onlyAgentItems = ["statistiche-agente"];

    if (isLoading) {
      return [];
    }

    const filterRecursive = (items: any[]): any[] => {
      return items
        .filter((item) => {
          if (user?.Kind === "Customer") {
            // If user is customer, hide agent items
            if (onlyAgentItems.includes(item.id)) return false;
          } else {
            // If user is NOT customer (Agent), hide customer items
            if (onlyCustomerItems.includes(item.id)) return false;
          }
          return true;
        })
        .map((item) => {
          if (item.children) {
            return { ...item, children: filterRecursive(item.children) };
          }
          return item;
        });
    };

    return filterRecursive(menuItems);
  }, [isLoading, user]);
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);
  const { currentPage } = useRouter();
  const navigate = useNavigate();
  const handleItemClick = (
    itemId: string,
    hasChildren: boolean,
    page?: string,
  ) => {
    if (hasChildren) {
      setExpandedItems((prev) =>
        prev.includes(itemId)
          ? prev.filter((id) => id !== itemId)
          : [...prev, itemId],
      );
    } else if (page) {
      return navigate(page as any);
    }
  };

  const renderMenuItem = (item: any, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);

    const Wrapper = item.page && !hasChildren ? NavLink : React.Fragment;
    const wrapperProps =
      item.page && !hasChildren ? { to: `/${item.page}` } : {};

    return (
      <Wrapper key={item.id} {...(wrapperProps as any)}>
        <ListItem disablePadding sx={{ pl: level * 2 }}>
          <ListItemButton
            selected={
              item.page && !hasChildren
                ? currentPage === item.page
                : expandedItems.includes(item.id)
            }
            onClick={() => handleItemClick(item.id, hasChildren, item.page)}
            sx={{
              minHeight: 48,
              px: 2.5,
              "&:hover": {
                backgroundColor: "primary.light",
                color: "white !important",
                "& .MuiListItemIcon-root": {
                  color: "white !important",
                },
                "& .MuiListItemText-primary": {
                  color: "white !important",
                },
              },
              "&.Mui-selected": {
                backgroundColor: "#666666",
                color: "white !important",
                "& .MuiListItemIcon-root": {
                  color: "white !important",
                },
                "& .MuiListItemText-primary": {
                  color: "white !important",
                },
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 3,
                justifyContent: "center",
                color:
                  (item.page && !hasChildren && currentPage === item.page) ||
                  expandedItems.includes(item.id)
                    ? "white"
                    : "text.primary",
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight:
                  (item.page && !hasChildren && currentPage === item.page) ||
                  expandedItems.includes(item.id)
                    ? 600
                    : 500,
                color:
                  (item.page && !hasChildren && currentPage === item.page) ||
                  expandedItems.includes(item.id)
                    ? "white"
                    : "text.primary",
              }}
            />
            {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child: any) =>
                renderMenuItem(child, level + 1),
              )}
            </List>
          </Collapse>
        )}
      </Wrapper>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        overflow: "auto",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header della Sidebar */}
      <Box
        sx={{
          p: 3,
          color: "white",
          textAlign: "center",
        }}
      >
        <div className="sidebar-logo">
          <img src="/eli_logo.png" alt="Eli Partner Program Logo" />
        </div>
      </Box>

      <Divider />

      {/* Menu Navigation */}
      <Box sx={{ flexGrow: 1, py: 1 }}>
        <List>{filteredMenuItems.map((item) => renderMenuItem(item))}</List>
      </Box>

      <Divider />

      {/* Footer della Sidebar */}
      <Box sx={{ p: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            selected={currentPage === "impostazioni"}
            onClick={() =>
              handleItemClick("impostazioni", false, "/impostazioni")
            }
            sx={{
              minHeight: 48,
              px: 2.5,
              "&:hover": {
                backgroundColor: "secondary.light",
                color: "white",
                "& .MuiListItemIcon-root": {
                  color: "white",
                },
              },
              "&.Mui-selected": {
                backgroundColor: "secondary.main",
                color: "white",
                "& .MuiListItemIcon-root": {
                  color: "white",
                },
                "&:hover": {
                  backgroundColor: "secondary.dark",
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 3,
                justifyContent: "center",
                color:
                  currentPage === "impostazioni" ? "white" : "text.primary",
              }}
            >
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Impostazioni"
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: currentPage === "impostazioni" ? 600 : 500,
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={isOpen}
      onClose={onToggle}
      sx={{
        width: 0,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: sidebarWidth,
          boxSizing: "border-box",
          borderRight: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          position: "fixed",
          zIndex: 999,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
